import React, { useEffect, useMemo, useRef, useState } from "react";
import { CircularProgressbar } from "react-circular-progressbar";
import { PhoenixStyledTooltip } from "src/Components/Dumb/PhoenixStyledTooltip";
import PhoenixEmptyState from "src/Components/UI/Phoenix/EmptyState/PhoenixEmptyState";
import { info, refresh } from "src/images/NewDesign";
import styled from "styled-components";
import { useDebounce } from "../../../utils/hooks";
import { theme } from "../../../utils/theme";
import { AppText, FlexDiv } from "../../UI";
import { PhoenixAppButton, PhoenixIcon, PhoenixInput } from "../../UI/Phoenix";
import { ProfileImage } from "../SettingSegments/ProfileImage";
import { AIProcessingCall } from "./AIProcessingCall";

type TranscriptMessage = {
  id: string;
  from: {
    id: string;
    name: string;
  };
  text: string;
  endTime: string;
  phrases: string[];
  duration: number;
  sentiment: {
    polarity: {
      score: number;
    };
    suggested: string;
  };
  startTime: string;
  timeOffset: number;
  conversationId: string;
  speaker: string;
  timestamp: string;
};

type Topic = {
  id: string;
  text: string;
  type: string;
  score: number;
  messageIds: string[];
  parentRefs: string[];
};

type Tracker = {
  name: string;
  messageRefs: { id: string }[];
};

interface TranscriptTabProps {
  aiProcessing: boolean;
  transcriptReady: boolean;
  aiData: {
    formattedTranscript?: TranscriptMessage[];
    trackers?: Tracker[];
    topics?: Topic[];
    talkTime?: Record<string, number>;
  };
  conferenceData: {
    recording_url_computed?: boolean;
  };
  handleSkipToTimestamp: (timestamp: number) => void;
  transcriptFromAbby: boolean;
}

type GroupedMessages = Record<string, TranscriptMessage[]>;

const groupMessagesByTracker = (formattedTranscript: TranscriptMessage[], trackers: Tracker[]): GroupedMessages => {
  return formattedTranscript.reduce((acc: GroupedMessages, t: TranscriptMessage) => {
    const tracker = getTrackerForMessage(t.id, trackers);
    if (tracker) {
      if (!acc[tracker]) {
        acc[tracker] = [];
      }
      acc[tracker].push(t);
    } else {
      if (!acc["untracked"]) {
        acc["untracked"] = [];
      }
      acc["untracked"].push(t);
    }
    return acc;
  }, {});
};

export const TranscriptTab: React.FC<TranscriptTabProps> = ({
  aiProcessing,
  transcriptReady,
  aiData,
  conferenceData,
  handleSkipToTimestamp,
  transcriptFromAbby,
}) => {
  const [selectedTrackers, setSelectedTrackers] = useState<Tracker[]>([]);
  const [selectedTopics, setSelectedTopics] = useState<Topic[]>([]);
  const [selectedSpeakers, setSelectedSpeakers] = useState<string[]>([]);
  const [highlightTextByTracker, setHighlightTextByTracker] = useState<boolean>(false);

  const [debouncedTranscriptSearch, setDebouncedTranscriptSearch] = useState<string>("");
  const [transcriptSearch, setTranscriptSearch] = useState<string>("");

  useDebounce(
    () => {
      setDebouncedTranscriptSearch(transcriptSearch);
    },
    [transcriptSearch],
    400,
  );

  const filteredTranscript = useMemo(() => {
    return aiData?.formattedTranscript?.filter((m: TranscriptMessage) => {
      // OR logic within selectedTopics
      const topicMatch = selectedTopics.length === 0 || selectedTopics.some((st) => st.messageIds.includes(m.id));

      // OR logic within selectedTrackers
      const trackerMatch =
        selectedTrackers.length === 0 || selectedTrackers.some((st) => st.messageRefs.some((mr) => mr.id === m.id));

      // OR logic within selectedSpeakers
      const speakerMatch = selectedSpeakers.length === 0 || selectedSpeakers.includes(m.speaker);

      // AND logic between groups
      return topicMatch && trackerMatch && speakerMatch;
    });
  }, [selectedTrackers, selectedTopics, selectedSpeakers, aiData]);

  const handleResetFilters = () => {
    setSelectedTrackers([]);
    setSelectedTopics([]);
    setSelectedSpeakers([]);
  };

  if (!conferenceData?.recording_url_computed) {
    return null;
  }

  return transcriptReady ? (
    <BodyContent>
      <TranscriptFilterContainer>
        <AppText
          fontSize={10}
          fontWeight={600}
          lineHeight={16}
          letterSpacing={1}
          uppercase
          color={theme.text.neutral.secondary}
        >
          Filter Transcript
        </AppText>

        {transcriptFromAbby && <AIProcessingCall />}
        {!transcriptFromAbby && (
          <TranscriptFilterBox label="Speakers" secondaryLabel="Talk Time">
            <FlexDiv direction="column" gap={8}>
              {Object.entries(aiData?.talkTime || {})?.map(([speaker, talkTime]: [string, number]) => (
                <SpeakerFilter
                  key={speaker}
                  speaker={speaker}
                  talkTime={talkTime}
                  selected={selectedSpeakers.includes(speaker)}
                  onClick={() =>
                    selectedSpeakers.includes(speaker)
                      ? setSelectedSpeakers(selectedSpeakers.filter((s: string) => s !== speaker))
                      : setSelectedSpeakers([...selectedSpeakers, speaker])
                  }
                />
              ))}
            </FlexDiv>
          </TranscriptFilterBox>
        )}

        {aiData?.trackers?.length ? (
          <TranscriptFilterBox label="Trackers">
            <FlexDiv gap={8} wrap="wrap">
              {aiData?.trackers?.map((tracker: Tracker) => (
                <Tracker
                  key={tracker.name}
                  tracker={tracker.name}
                  selected={!!selectedTrackers.find((st: Tracker) => st.name === tracker.name)}
                  onClick={() =>
                    !!selectedTrackers.find((st: Tracker) => st.name === tracker.name)
                      ? setSelectedTrackers(selectedTrackers.filter((st: Tracker) => st.name !== tracker.name))
                      : setSelectedTrackers([...selectedTrackers, tracker])
                  }
                />
              ))}
            </FlexDiv>
          </TranscriptFilterBox>
        ) : null}

        {aiData?.topics?.length ? (
          <TranscriptFilterBox
            label="Topics"
            // need real tooltip text from Product
            // tooltipText="Filter the transcript by automatically extracted topics and themes using AssemblyAI's advanced language models. Selecting a topic will highlight the relevant parts of the conversation."
          >
            <FlexDiv gap={8} wrap="wrap">
              {aiData?.topics?.map((topic: Topic) => (
                <Topic
                  key={topic.text}
                  topic={topic.text}
                  selected={!!selectedTopics.find((st: Topic) => st.id === topic.id)}
                  onClick={() =>
                    !!selectedTopics.find((st: Topic) => st.id === topic.id)
                      ? setSelectedTopics(selectedTopics.filter((st: Topic) => st.id !== topic.id))
                      : setSelectedTopics([...selectedTopics, topic])
                  }
                />
              ))}
            </FlexDiv>
          </TranscriptFilterBox>
        ) : null}
      </TranscriptFilterContainer>
      <TranscriptContainer>
        <TranscriptSearchContainer>
          <div style={{ width: "100%", maxWidth: "900px" }}>
            <PhoenixInput
              placeholder="Search Transcript"
              variant="primary"
              type="text"
              searchInput
              fontSize={10}
              fontWeight={400}
              displayNoContextText
              value={transcriptSearch}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTranscriptSearch(e.target.value)}
              height={40}
            />
          </div>
        </TranscriptSearchContainer>
        <TranscriptContent
          aiData={aiData}
          debouncedTranscriptSearch={debouncedTranscriptSearch}
          filteredTranscript={filteredTranscript || []}
          handleSkipToTimestamp={handleSkipToTimestamp}
          highlightTextByTracker={highlightTextByTracker}
          transcriptFromAbby={transcriptFromAbby}
          handleResetFilters={handleResetFilters}
        />
      </TranscriptContainer>
    </BodyContent>
  ) : (
    <AIProcessingCall />
  );
};

interface TranscriptContentProps {
  aiData: {
    formattedTranscript?: TranscriptMessage[];
    trackers?: Tracker[];
    topics?: Topic[];
    talkTime?: Record<string, number>;
  };
  debouncedTranscriptSearch: string;
  filteredTranscript: TranscriptMessage[];
  handleSkipToTimestamp: (timestamp: number) => void;
  highlightTextByTracker: boolean;
  transcriptFromAbby: boolean;
  handleResetFilters: () => void;
}

const TranscriptContent: React.FC<TranscriptContentProps> = ({
  aiData,
  debouncedTranscriptSearch,
  filteredTranscript,
  handleSkipToTimestamp,
  highlightTextByTracker,
  transcriptFromAbby,
  handleResetFilters,
}) => {
  const groupedMessages = useMemo(
    () => groupMessagesByTracker(aiData?.formattedTranscript || [], aiData?.trackers || []),
    [aiData?.formattedTranscript, aiData?.trackers],
  );

  const hasNoMessagesToShow = useMemo(() => {
    // Check if all filtered messages across all tracker groups have nothing to show
    if (!groupedMessages || Object.keys(groupedMessages).length === 0) return true;

    return Object.entries(groupedMessages).every(([_, messages]) => {
      if (!Array.isArray(messages)) return true;

      const filteredMessages = messages.filter((t: TranscriptMessage) =>
        t?.text?.toLowerCase()?.includes(debouncedTranscriptSearch.toLowerCase()),
      );

      return filteredMessages.every((t: TranscriptMessage) => !filteredTranscript?.find((ft) => ft?.id === t.id));
    });
  }, [groupedMessages, debouncedTranscriptSearch, filteredTranscript]);

  if (hasNoMessagesToShow) {
    return <NoMessages resetFilters={handleResetFilters} />;
  }

  return (
    <>
      {Object.entries(groupedMessages).map(([tracker, messages]) => {
        if (!Array.isArray(messages)) return null;

        // Filter out any messages that don't match the search
        const filteredMessages = messages.filter((t: TranscriptMessage) =>
          t?.text?.toLowerCase()?.includes(debouncedTranscriptSearch.toLowerCase()),
        );

        // Check if all messages are filtered out (so we don't show the tracker group completely)
        const hasNoMessageToShow = filteredMessages.every(
          (t: TranscriptMessage) => !filteredTranscript?.find((ft) => ft?.id === t.id),
        );

        if (hasNoMessageToShow) return null;

        return (
          <TrackerGroup key={tracker} tracker={tracker}>
            {filteredMessages.map((t: TranscriptMessage, i: number) => (
              <TranscriptRow
                key={`${t?.text}-${i}`}
                text={t?.text}
                speaker={t?.speaker}
                timestamp={t?.timestamp}
                show={!!filteredTranscript?.find((ft) => ft?.id === t.id)}
                index={i}
                handleSkipToTimestamp={() => handleSkipToTimestamp(Number(t?.timestamp))}
                highlightText={highlightTextByTracker}
                tracker={tracker}
              />
            ))}
          </TrackerGroup>
        );
      })}
      {transcriptFromAbby && (
        <FlexDiv height={100} justify="center" align="center">
          <AIProcessingCall showText={false} />
        </FlexDiv>
      )}
    </>
  );
};

interface NoMessagesProps {
  resetFilters: () => void;
}

const NoMessages: React.FC<NoMessagesProps> = ({ resetFilters }) => {
  return (
    <FlexDiv justify="center" align="center" padding="40px 0px">
      <PhoenixEmptyState
        text="No results match your search criteria."
        subtext="Reset your filters to see more results or start a new search"
        iconType="transcript"
        button={
          <PhoenixAppButton variant="brand-outline" buttonType="ghost-small" onClick={resetFilters}>
            <PhoenixIcon svg={refresh} size={16} variant="brand" />
            Reset Filters
          </PhoenixAppButton>
        }
      />
    </FlexDiv>
  );
};

const BodyContent = styled.div`
  height: 100%;
  overflow-y: auto;
  overflow-x: hidden;
  height: 100%;
`;
interface TranscriptRowProps {
  text: string;
  speaker: string;
  timestamp: string;
  tracker: string;
  show: boolean;
  index: number;
  handleSkipToTimestamp: () => void;
  highlightText: boolean;
}

const TranscriptRow: React.FC<TranscriptRowProps> = ({
  text,
  speaker,
  timestamp,
  tracker,
  show,
  handleSkipToTimestamp,
  highlightText,
}) => {
  const rowRef = useRef<HTMLDivElement>(null);

  const color = getTrackerColor(tracker);
  return (
    <TranscriptRowContainer ref={rowRef} show={show}>
      <AppText
        fontSize={12}
        fontWeight={400}
        lineHeight={18}
        cursor="pointer"
        showBorderOnClick={false}
        style={{ minWidth: "72px", maxWidth: "72px" }}
        onClick={handleSkipToTimestamp}
        color={theme.text.brand.primary}
      >
        {timestamp}
      </AppText>
      <FlexDiv direction="column" gap={8}>
        <FlexDiv align="center" gap={8}>
          <ProfileImage diameter={24} borderWidth={0} borderDiameter={24} />
          <AppText fontSize={12} fontWeight={500} lineHeight={18}>
            {speaker}
          </AppText>
        </FlexDiv>
        <AppText fontSize={12} fontWeight={400} lineHeight={18} color={highlightText ? color : theme.BLACK_COLOR}>
          {text}
        </AppText>
      </FlexDiv>
    </TranscriptRowContainer>
  );
};

const TranscriptRowContainer = styled.div<{ show: boolean }>`
  display: flex;
  gap: 8px;
  padding-left: 24px;
  overflow: hidden;

  margin-bottom: ${({ show }) => (show ? "24px" : "0")};

  transition: min-height 0.2s ease-out, max-height 0.2s ease-out, margin 0.2s ease-out;
`;

const TranscriptContainer = styled.div`
  display: flex;
  flex-direction: column;

  width: 100%;
  height: 100%;
  overflow-y: auto;

  padding: 0px 0px 16px 0px;
  margin-top: 16px;
  position: relative;
`;

const TranscriptSearchContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 16px 40px;
  background-color: ${theme.fill.neutral.primary};
  position: sticky;
  top: 0;
  z-index: 1;
`;

const TranscriptFilterContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;

  width: 100%;
  height: max-content;

  padding: 24px;

  border-right: 1px solid ${theme.border.neutral.secondary};
`;

interface TranscriptFilterBoxProps {
  label: string;
  secondaryLabel?: string;
  children: React.ReactNode;
  rightTextComponent?: React.ReactNode;
  tooltipText?: string;
}

const TranscriptFilterBox: React.FC<TranscriptFilterBoxProps> = ({
  label,
  secondaryLabel,
  children,
  rightTextComponent,
  tooltipText,
}) => {
  return (
    <>
      {tooltipText && <PhoenixStyledTooltip id={`transcript-filter-tooltip`} />}
      <FlexDiv
        direction="column"
        gap={16}
        style={{ padding: "16px 0px 24px 0px", borderTop: `1px solid ${theme.border.neutral.secondary}` }}
      >
        <FlexDiv justify="space-between">
          <FlexDiv align="center" gap={8}>
            <AppText fontSize={14} fontWeight={500} lineHeight={20}>
              {label}
            </AppText>
            {tooltipText && (
              <PhoenixIcon
                svg={info}
                size={16}
                variant="brand"
                data-tip={tooltipText}
                data-for={`transcript-filter-tooltip`}
              />
            )}
          </FlexDiv>
          {rightTextComponent}
          {secondaryLabel && (
            <AppText fontSize={12} fontWeight={500} lineHeight={18} color={theme.text.neutral.secondary}>
              {secondaryLabel}
            </AppText>
          )}
        </FlexDiv>
        <div>{children}</div>
      </FlexDiv>
    </>
  );
};

interface SpeakerFilterProps {
  talkTime: number;
  speaker: string;
  selected?: boolean;
  onClick?: () => void;
}

const SpeakerFilter: React.FC<SpeakerFilterProps> = ({ talkTime, speaker, selected, onClick }) => {
  const [tt, setTT] = useState(0);
  useEffect(() => {
    const timeout = setTimeout(() => {
      setTT(talkTime);
    }, 0);
    return () => clearTimeout(timeout);
  }, []);

  return (
    <SpeakerFilterContainer selected={selected} onClick={onClick}>
      <FlexDiv align="center" gap={8}>
        <ProfileImage diameter={24} borderWidth={0} borderDiameter={24} />
        <AppText fontSize={12} fontWeight={500} lineHeight={18}>
          {speaker}
        </AppText>
      </FlexDiv>

      <FlexDiv align="center" gap={8} style={{ minWidth: "56px", maxWidth: "56px" }}>
        <div style={{ minWidth: "24px", maxWidth: "24px", minHeight: "24px", maxHeight: "24px" }}>
          <CircularProgressbar
            value={tt}
            strokeWidth={12}
            styles={{
              path: {
                stroke: theme.fill.brand.primary,
                strokeLinecap: "round",
                transitionDuration: "0.5s",
              },
              trail: {
                stroke: theme.fill.neutral.tertiary,
              },
            }}
          />
        </div>
        <AppText
          fontSize={10}
          fontWeight={500}
          lineHeight={14}
          color={theme.text.neutral.secondary}
          style={{ marginRight: "auto" }}
        >
          {talkTime}%
        </AppText>
      </FlexDiv>
    </SpeakerFilterContainer>
  );
};

const SpeakerFilterContainer = styled.div<{ selected?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: center;

  width: 100%;
  height: 40px;

  padding: 8px;

  background-color: ${({ selected }) => (selected ? theme.fill.brand.tertiary : "none")};
  border: 1px solid ${({ selected }) => (selected ? theme.border.brand.primary : theme.border.neutral.secondary)};
  border-radius: 8px;

  :hover {
    background-color: ${theme.fill.brand.tertiary};
  }
  transition: background-color 0.15s ease-in-out, border 0.1s ease-in-out;
  cursor: pointer;
`;

const Tracker: React.FC<{ tracker: string; selected: boolean; onClick: () => void }> = ({
  tracker,
  selected,
  onClick,
}) => {
  const color = getTrackerColor(tracker);
  return (
    <TrackerContainer selected={selected} color={color} onClick={onClick}>
      <TrackerText fontSize={8} fontWeight={600} lineHeight={12} uppercase selected={selected} textColor={color}>
        {tracker}
      </TrackerText>
    </TrackerContainer>
  );
};

const TrackerText = styled(AppText)<{ selected: boolean; textColor: string }>`
  color: ${(props) => (props.selected ? theme.text.neutral.inverse : props.textColor)};
  transition: color 0.15s ease-in-out;
`;

const TrackerContainer = styled.div<{ selected: boolean; color: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid ${(props) => props.color};
  height: 24px;
  padding: 4px 8px;

  border-radius: 4px;
  background-color: ${(props) => (props.selected ? props.color : "none")};
  cursor: pointer;

  transition: background-color 0.15s ease-in-out;

  :hover {
    background-color: ${(props) => props.color};
    ${TrackerText} {
      color: ${theme.text.neutral.inverse};
    }
  }
`;

const Topic: React.FC<{ topic: string; selected: boolean; onClick: () => void }> = ({ topic, selected, onClick }) => {
  return (
    <TopicContainer selected={selected} onClick={onClick}>
      <TopicText fontSize={8} fontWeight={600} lineHeight={12} uppercase selected={selected}>
        {topic}
      </TopicText>
    </TopicContainer>
  );
};

const TopicText = styled(AppText)<{ selected: boolean }>`
  color: ${(props) => (props.selected ? theme.text.neutral.inverse : theme.text.neutral.tertiary)};
  transition: color 0.15s ease-in-out;
`;

const TopicContainer = styled.div<{ selected: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;

  height: 24px;
  padding: 4px 8px;

  border: 1px solid ${theme.fill.neutral.quaternary};
  border-radius: 4px;
  background-color: ${(props) => (props.selected ? theme.fill.neutral.quaternary : "none")};
  cursor: pointer;

  transition: background-color 0.15s ease-in-out;

  :hover {
    background-color: ${theme.fill.neutral.quaternary};
    ${TopicText} {
      color: ${theme.text.neutral.inverse};
    }
  }
`;

const TrackerGroup = styled.div<{ tracker: string }>`
  margin: 8px;
  border-radius: 8px;
  padding: 8px;
  transition: border 0.15s ease-in-out;
`;

const getTrackerForMessage = (messageId: string, trackers: Tracker[]): string | null => {
  const tracker = trackers.find((t) => t.messageRefs.some((mr) => mr.id === messageId));
  return tracker ? tracker.name : null;
};

const getTrackerColor = (tracker: string): string => {
  const colors = [
    theme.border.success.inverse,
    theme.border.dataviz1.primary,
    theme.border.dataviz4.inverse,
    theme.border.dataviz2.primary,
    theme.border.success.primary,
  ];

  switch (tracker) {
    case "Setting the Demo":
      return colors[0];
    case "Gatekeeper Intro":
      return colors[1];
    case "Confirming The Demo":
      return colors[2];
    case "30 Second Pitch":
      return colors[3];
    case "Decision Maker Intro":
      return colors[4];
    default:
      return colors[Math.floor(Math.random() * colors.length)];
  }
};
